<template>
  <AutoComplete
    ref="autoCompleteRef"
    v-model="searchModel"
    :input-id="id"
    :disabled="disabled"
    :loading="locations.loading"
    :suggestions="suggestions ?? locations.data"
    :append-to="appendTo"
    :invalid="!!errors.length"
    :pt="{
      pcInputText: {
        root: {
          autofocus: autofocus,
        },
      },
    }"
    :dropdown="!disabled"
    :show-clear="!disabled"
    class="location-quick-search"
    @clear="onClear"
    @complete="onLocationSearch($event.query)"
    @item-select="onItemSelected($event.value)"
    @value-change="onValueChange"
  >
    <template v-if="slots.option" #option="{ index, option }">
      <slot name="option" :index="index" :option="option" />
    </template>
  </AutoComplete>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';

import { Address, Loadable, Location } from '@rygen/rygen-ui/model';

import AutoComplete from 'primevue/autocomplete';

type AddressKey = keyof Address;
type LocationKey = keyof Location;
type SearchableLocationKey = Extract<LocationKey, 'name' | 'code'>;
export type LocationSearchField = SearchableLocationKey | AddressKey;

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Location,
    default: () => new Location(),
  },
  searchField: {
    type: String as PropType<LocationSearchField>,
    default: () => 'name',
  },
  locations: {
    type: Object as PropType<Loadable<Location[]>>,
    default: () => ({ loading: false }),
  },
  /**
   * Array of suggestions to render in the dropdown
   *
   * This prop is optional and only needs to be used if you are grouping
   * results. You should also provide `Autocomplete`'s `option-group-*` props
   * to indicate how to render the provided suggestions
   */
  suggestions: {
    type: Array as PropType<any[]>,
    default: () => undefined,
  },
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  autofocus: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  modelValue,
  searchField,
  locations,
  suggestions,
  errors,
  appendTo,
  disabled,
  autofocus,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'clear'): void;
  (event: 'update:model-value', newVal: Location): void;
  (event: 'search', newVal: string): void;
  (event: 'select', newVal: Location): void;
}>();

const slots = defineSlots<{
  option?: (props: { index: number; option: any }) => any;
}>();

const autoCompleteRef: Ref<typeof AutoComplete | null> = ref(null);
const internalValue: Ref<Location> = ref(new Location(modelValue.value));

const searchModel = computed({
  get() {
    return searchField.value === 'name' || searchField.value === 'code'
      ? internalValue.value[searchField.value]
      : internalValue.value.address?.[searchField.value];
  },
  set(newVal) {
    if (typeof newVal !== 'string' && typeof newVal !== 'undefined') {
      return;
    }

    if (searchField.value === 'name' || searchField.value === 'code') {
      internalValue.value[searchField.value] = newVal;
    } else {
      const address = internalValue.value.address ?? new Address();
      address[searchField.value] = newVal;
      internalValue.value.address = address;
    }
  },
});

const onLocationSearch = async (query: string) => {
  const input = query || searchModel.value;
  emit('search', input ?? '');
};

const onItemSelected = async (selection: Location) => {
  const newVal = new Location(selection);
  internalValue.value = newVal;
  emit('select', newVal);
};

const onClear = () => {
  if (internalValue.value.id) {
    emit('clear');
  } else {
    searchModel.value = undefined;
  }
};

/**
 * Hack to get around `AutoComplete` not emitting it's `clear` event
 *
 * This function checks if the value is nullish and emits a `clear` event,
 * otherwise it ignores the change.
 */
const onValueChange = (newVal: string) => {
  if (!newVal) {
    onClear();
  }
};

const clearSearchTimeout = () => {
  // Cancel 'complete' callback in autocomplete component
  // This handles the case where the user enters or pastes a value and then
  // quickly clicks or tabs away.
  // Without clearing the timeout, the 'complete' callback will be called
  // which will invoke the search which will then open the dropdown, even though
  // the user is no longer on this component at all.
  // This is totally abusing the internal API of the autocomplete component, I
  // fully expect it to break in the future.
  const searchTimeout = (autoCompleteRef.value as any)?.searchTimeout;
  clearTimeout(searchTimeout);
};

watch(
  modelValue,
  (newVal: Location) => {
    internalValue.value = new Location(newVal);
  },
  { deep: true, immediate: true },
);

watch(
  internalValue,
  (newVal: Location) => {
    if (!isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);

defineExpose({
  clearSearchTimeout,
});
</script>

<style>
.location-quick-search {
  .p-autocomplete-input {
    width: 100%;
  }
}
</style>
